
import Dexie, { Table } from 'dexie';
import {
    Farm,
    OMTask,
    Transaction,
    Document,
    CorrespondenceMessage,
    RecordLink,
    MapFeature,
    Asset,
    MutationOperation,
    SyncState,
    GpsTrackPoint,
    ScoutingPhoto,
    ScoutingPhotoCategory,
    ScoutingPhotoCategoryLink,
    Project,
    WorkLog,
} from '../types.js';

export class SonataDatabase extends Dexie {
    farms!: Table<Farm>;
    tasks!: Table<OMTask>;
    transactions!: Table<Transaction>;
    documents!: Table<Document>;
    messages!: Table<CorrespondenceMessage>;
    links!: Table<RecordLink>;
    mapFeatures!: Table<MapFeature>;
    assets!: Table<Asset>;
    projects!: Table<Project>;
    workLogs!: Table<WorkLog>;
    gpsTrackPoints!: Table<GpsTrackPoint>;
    recordedRoutes!: Table<import('./routeRecorder').RecordedRoute>;
    photoUploads!: Table<import('./photos/uploadTypes').RetainedUpload>;
    scoutingPhotos!: Table<ScoutingPhoto>;
    scoutingPhotoCategories!: Table<ScoutingPhotoCategory>;
    scoutingPhotoCategoryLinks!: Table<ScoutingPhotoCategoryLink>;
    
    // Sync Tables
    outbox!: Table<MutationOperation>;
    syncState!: Table<SyncState>;
    workspaceMeta!: Table<{ key: string; value: any }>;

    constructor(name = 'SonataFieldsDB') {
        super(name);
        
        // Define Schema
        // Version 3: Added gpsTrackPoints for on-device GPS tracking
        (this as any).version(3).stores({
            contacts: 'contact_id, type, deleted_at, updated_at',
            farms: 'farm_id, deleted_at, updated_at',
            tracts: 'tract_id, farm_id, deleted_at, updated_at',
            fields: 'field_id, tract_id, deleted_at, updated_at',
            contracts: 'contract_id, farm_id, participant_contact_id, status, type, deleted_at, updated_at',
            contractPractices: 'contract_practice_id, contract_id, cp_id, deleted_at, updated_at',
            contractFields: 'contract_field_id, contract_practice_id, field_id, deleted_at, updated_at',
            tasks: 'task_id, related_entity_type, related_entity_id, contract_field_id, asset_id, scheduled_date, deleted_at, updated_at',
            transactions: 'transaction_id, contract_id, asset_id, farm_id, date, type, category, deleted_at, updated_at',
            complianceEvents: 'event_id, contract_field_id, deleted_at, updated_at',
            categories: 'category_id, deleted_at, updated_at',
            practices: 'cp_id, category_id, deleted_at, updated_at', 
            documents: 'document_id, source, deleted_at, updated_at',
            messages: 'message_id, channel, deleted_at, updated_at',
            links: 'record_link_id, from_record_type, from_record_id, to_record_type, to_record_id, entity_type, entity_id, document_id, message_id, deleted_at, updated_at',
            mapFeatures: 'map_feature_id, farm_id, entity_type, entity_id, feature_type, deleted_at, updated_at',
            assets: 'asset_id, status, deleted_at, updated_at',
            gpsTrackPoints: 'id, ts, sessionId',
            
            // New Sync Tables
            outbox: 'op_id, created_at, status', // Index on status to easily find pending items
            syncState: 'key' // Singleton key
        });

        // Version 4: Add scouting photos + categories for gallery
        (this as any).version(4).stores({
            contacts: 'contact_id, type, deleted_at, updated_at',
            farms: 'farm_id, deleted_at, updated_at',
            tracts: 'tract_id, farm_id, deleted_at, updated_at',
            fields: 'field_id, tract_id, deleted_at, updated_at',
            contracts: 'contract_id, farm_id, participant_contact_id, status, type, deleted_at, updated_at',
            contractPractices: 'contract_practice_id, contract_id, cp_id, deleted_at, updated_at',
            contractFields: 'contract_field_id, contract_practice_id, field_id, deleted_at, updated_at',
            tasks: 'task_id, related_entity_type, related_entity_id, contract_field_id, asset_id, scheduled_date, deleted_at, updated_at',
            transactions: 'transaction_id, contract_id, asset_id, farm_id, date, type, category, deleted_at, updated_at',
            complianceEvents: 'event_id, contract_field_id, deleted_at, updated_at',
            categories: 'category_id, deleted_at, updated_at',
            practices: 'cp_id, category_id, deleted_at, updated_at', 
            documents: 'document_id, source, deleted_at, updated_at',
            messages: 'message_id, channel, deleted_at, updated_at',
            links: 'record_link_id, from_record_type, from_record_id, to_record_type, to_record_id, entity_type, entity_id, document_id, message_id, photo_id, deleted_at, updated_at',
            mapFeatures: 'map_feature_id, farm_id, entity_type, entity_id, feature_type, deleted_at, updated_at',
            assets: 'asset_id, status, deleted_at, updated_at',
            gpsTrackPoints: 'id, ts, sessionId',
            scoutingPhotos: 'photo_id, category_id, uploaded_at, deleted_at, updated_at',
            scoutingPhotoCategories: 'category_id, deleted_at, updated_at',
            scoutingPhotoCategoryLinks: '[photo_id+category_id], photo_id, category_id, deleted_at, updated_at',
            
            // New Sync Tables
            outbox: 'op_id, created_at, status', // Index on status to easily find pending items
            syncState: 'key' // Singleton key
        });

        // Version 5: Add projects + work logs for project timers
        (this as any).version(5).stores({
            contacts: 'contact_id, type, deleted_at, updated_at',
            farms: 'farm_id, deleted_at, updated_at',
            tracts: 'tract_id, farm_id, deleted_at, updated_at',
            fields: 'field_id, tract_id, deleted_at, updated_at',
            contracts: 'contract_id, farm_id, participant_contact_id, status, type, deleted_at, updated_at',
            contractPractices: 'contract_practice_id, contract_id, cp_id, deleted_at, updated_at',
            contractFields: 'contract_field_id, contract_practice_id, field_id, deleted_at, updated_at',
            tasks: 'task_id, related_entity_type, related_entity_id, contract_field_id, asset_id, scheduled_date, deleted_at, updated_at',
            transactions: 'transaction_id, contract_id, asset_id, farm_id, date, type, category, deleted_at, updated_at',
            complianceEvents: 'event_id, contract_field_id, deleted_at, updated_at',
            categories: 'category_id, deleted_at, updated_at',
            practices: 'cp_id, category_id, deleted_at, updated_at', 
            documents: 'document_id, source, deleted_at, updated_at',
            messages: 'message_id, channel, deleted_at, updated_at',
            links: 'record_link_id, from_record_type, from_record_id, to_record_type, to_record_id, entity_type, entity_id, document_id, message_id, photo_id, deleted_at, updated_at',
            mapFeatures: 'map_feature_id, farm_id, entity_type, entity_id, feature_type, deleted_at, updated_at',
            assets: 'asset_id, status, deleted_at, updated_at',
            projects: 'id, status, priority_rank, updated_at, deleted_at',
            workLogs: 'id, project_id, user_id, state, start_time, end_time, updated_at, deleted_at',
            gpsTrackPoints: 'id, ts, sessionId',
            scoutingPhotos: 'photo_id, category_id, uploaded_at, deleted_at, updated_at',
            scoutingPhotoCategories: 'category_id, deleted_at, updated_at',
            scoutingPhotoCategoryLinks: '[photo_id+category_id], photo_id, category_id, deleted_at, updated_at',
            
            // New Sync Tables
            outbox: 'op_id, created_at, status',
            syncState: 'key'
        });

        (this as any).version(6).stores({
            contacts: 'contact_id, type, deleted_at, updated_at',
            farms: 'farm_id, deleted_at, updated_at',
            tracts: 'tract_id, farm_id, deleted_at, updated_at',
            fields: 'field_id, tract_id, deleted_at, updated_at',
            contracts: 'contract_id, farm_id, participant_contact_id, status, type, deleted_at, updated_at',
            contractPractices: 'contract_practice_id, contract_id, cp_id, deleted_at, updated_at',
            contractFields: 'contract_field_id, contract_practice_id, field_id, deleted_at, updated_at',
            tasks: 'task_id, related_entity_type, related_entity_id, contract_field_id, asset_id, scheduled_date, deleted_at, updated_at',
            transactions: 'transaction_id, contract_id, asset_id, farm_id, date, type, category, deleted_at, updated_at',
            complianceEvents: 'event_id, contract_field_id, deleted_at, updated_at',
            categories: 'category_id, deleted_at, updated_at',
            practices: 'cp_id, category_id, deleted_at, updated_at',
            documents: 'document_id, source, deleted_at, updated_at',
            messages: 'message_id, channel, deleted_at, updated_at',
            links: 'record_link_id, from_record_type, from_record_id, to_record_type, to_record_id, entity_type, entity_id, document_id, message_id, photo_id, deleted_at, updated_at',
            mapFeatures: 'map_feature_id, farm_id, entity_type, entity_id, feature_type, deleted_at, updated_at',
            assets: 'asset_id, status, deleted_at, updated_at',
            projects: 'id, status, priority_rank, updated_at, deleted_at',
            workLogs: 'id, project_id, user_id, state, start_time, end_time, updated_at, deleted_at',
            gpsTrackPoints: 'id, ts, sessionId',
            scoutingPhotos: 'photo_id, category_id, uploaded_at, deleted_at, updated_at',
            scoutingPhotoCategories: 'category_id, deleted_at, updated_at',
            scoutingPhotoCategoryLinks: '[photo_id+category_id], photo_id, category_id, deleted_at, updated_at',
            outbox: 'op_id, created_at, status',
            syncState: 'key'
        }).upgrade(async (tx: any) => {
            const links = tx.table('links');
            await links.toCollection().modify((link: any) => {
                if (!link.from_record_type || !link.from_record_id || !link.to_record_type || !link.to_record_id) {
                    const primaryType = link.entity_type || 'unknown';
                    const primaryId = link.entity_id || '';
                    const secondaryType = link.document_id
                        ? 'document'
                        : link.photo_id
                            ? 'scouting_photo'
                            : link.message_id
                                ? 'message'
                                : primaryType;
                    const secondaryId = link.document_id || link.photo_id || link.message_id || primaryId;
                    link.from_record_type = primaryType;
                    link.from_record_id = primaryId;
                    link.to_record_type = secondaryType;
                    link.to_record_id = secondaryId;
                }
            });
        });

        // Version 7: Retire legacy contracts/contact/location lookup tables from active Dexie schema.
        (this as any).version(7).stores({
            contacts: null,
            tracts: null,
            fields: null,
            contracts: null,
            contractPractices: null,
            contractFields: null,
            complianceEvents: null,
            categories: null,
            practices: null,
            farms: 'farm_id, deleted_at, updated_at',
            tasks: 'task_id, related_entity_type, related_entity_id, contract_field_id, asset_id, scheduled_date, deleted_at, updated_at',
            transactions: 'transaction_id, contract_id, asset_id, farm_id, date, type, category, deleted_at, updated_at',
            documents: 'document_id, source, deleted_at, updated_at',
            messages: 'message_id, channel, deleted_at, updated_at',
            links: 'record_link_id, from_record_type, from_record_id, to_record_type, to_record_id, entity_type, entity_id, document_id, message_id, photo_id, deleted_at, updated_at',
            mapFeatures: 'map_feature_id, farm_id, entity_type, entity_id, feature_type, deleted_at, updated_at',
            assets: 'asset_id, status, deleted_at, updated_at',
            projects: 'id, status, priority_rank, updated_at, deleted_at',
            workLogs: 'id, project_id, user_id, state, start_time, end_time, updated_at, deleted_at',
            gpsTrackPoints: 'id, ts, sessionId',
            scoutingPhotos: 'photo_id, category_id, uploaded_at, deleted_at, updated_at',
            scoutingPhotoCategories: 'category_id, deleted_at, updated_at',
            scoutingPhotoCategoryLinks: '[photo_id+category_id], photo_id, category_id, deleted_at, updated_at',
            outbox: 'op_id, created_at, status',
            syncState: 'key'
        });
        (this as any).version(8).stores({ workspaceMeta: 'key', outbox: 'op_id, created_at_local, status, [table_name+entity_id]' });
        (this as any).version(9).stores({recordedRoutes:'id, farmId, ownerEmail, startedAt, deleted_at',gpsTrackPoints:'id, ts, sessionId, routeId'});
        (this as any).version(10).stores({photoUploads:'id, account, farmId, state, createdAt'});
        // Byte-backed originals require a compatible reader. Keep old Blob rows
        // untouched; recover only an explicitly used original outside an IDB tx.
        (this as any).version(11).stores({photoUploads:'id, account, farmId, state, createdAt'});
    }
}

export const normalizeWorkspaceAccount = (account: string | null) => account?.trim().toLowerCase() || null;
export const workspaceDatabaseName = (account: string | null) => `SonataFieldsDB-account-${encodeURIComponent(normalizeWorkspaceAccount(account) || 'signed-out')}`;
const databases = new Map<string, SonataDatabase>();
export const databaseForAccount = (account: string | null) => {
    const name = workspaceDatabaseName(account);
    if (!databases.has(name)) databases.set(name, new SonataDatabase(name));
    return databases.get(name)!;
};
const initialAccount = () => { try { const session = localStorage.getItem('sonata_account_session_v2'); return session ? JSON.parse(session).email : localStorage.getItem('sonata_current_user_email'); } catch { return null; } };
export let workspaceAccount = normalizeWorkspaceAccount(initialAccount());
export let db = databaseForAccount(workspaceAccount);
export const selectWorkspaceAccount = (account: string | null) => {
    workspaceAccount = normalizeWorkspaceAccount(account);
    db = databaseForAccount(workspaceAccount);
};

// Unowned legacy stores are retained for explicit recovery, never assigned to a sign-in.
export const migrateFromLocalStorage = async () => {};
